<template>
  <div class="w-100 mb-4">
    <!-- Datos del lead -->
    <div class="card mb-4">
      <div class="card-header pb-0">
        <h6 class="text-uppercase" v-if="user">Detalles de {{ user.type }}</h6>
      </div>
      <div class="card-body px-4">
        <div class="row" v-if="user">
          <div class="col-md-6 col-xl-3 mb-3">
            <label class="font-weight-bold">Nombre</label>
            <input type="text" class="form-control" v-model="user.name" />
          </div>
          <div class="col-md-6 col-xl-3 mb-3">
            <label class="font-weight-bold">Correo</label>
            <input type="text" class="form-control" v-model="user.email" />
          </div>
          <div class="col-md-6 col-xl-3 mb-3">
            <label class="font-weight-bold">Identificación</label>
            <input type="text" class="form-control" v-model="user.identification" />
          </div>
          <div class="col-md-6 col-xl-3 mb-3">
            <label class="font-weight-bold">Teléfono</label>
            <input type="text" class="form-control" v-model="user.phone" />
          </div>
          <div class="col-md-6 col-xl-3 mb-3">
            <label class="font-weight-bold">País</label>
            <input type="text" class="form-control" v-model="user.country" />
          </div>
          <div class="col-md-6 col-xl-3 mb-3">
            <label class="font-weight-bold">Campaña</label>
            <input type="text" class="form-control" v-model="user.campaign" />
          </div>
          <div class="col-md-6 col-xl-3 mb-3">
            <label class="font-weight-bold">WhatsApp Asesor</label>
            <input
              type="text"
              class="form-control"
              v-model="user.whatsapp_asesor"
              placeholder="Número de WhatsApp"
            />
          </div>
          <div class="col-md-6 col-xl-3 mb-3">
            <label class="font-weight-bold">Estado</label>
            <select
              v-if="$userCan(['administrator'])"
              class="form-control"
              v-model="user.status"
            >
              <option :value="true">Activo</option>
              <option :value="false">Inactivo</option>
            </select>
            <p
              v-else
              class="form-control mb-1"
              :class="user.status ? 'text-success' : 'text-danger'"
            >
              {{ user.status ? "ACTIVO" : "PENDIENTE" }}
            </p>
            <span v-if="!user.status" class="badge bg-warning text-dark">
              Pendiente de activación por el administrador
            </span>
          </div>
          <div
            class="col-md-6 col-xl-3 mb-3"
            v-if="$userCan(['administrator', 'supervisor'])"
          >
            <label class="font-weight-bold">Agente asignado</label>
            <div class="d-flex">
              <select class="form-control me-2" v-model="user.assigned_agent">
                <option value="">Sin asignar</option>
                <option v-for="a in agents" :key="a._id" :value="a._id">
                  {{ a.name }}
                </option>
              </select>
              <button
                class="btn btn-outline-success mb-0 px-3"
                @click="assignAgent"
              >
                Guardar
              </button>
            </div>
          </div>
          <div class="col-md-6 col-xl-3 mb-3">
            <label class="font-weight-bold">Fecha de creación</label>
            <p class="form-control mb-0">
              {{ $filters.formatDate(user.created_at) }}
            </p>
          </div>
          <div class="col-md-6 col-xl-3 mb-3">
            <label class="font-weight-bold">Token (click para copiar)</label>
            <p
              class="form-control mb-0 text-danger cursor-pointer text-truncate"
              @click="copyToClipboard(user._id)"
              title="Haz clic para copiar al portapapeles"
            >
              {{ user._id }}
            </p>
          </div>
          <div class="col-12 mt-2">
            <button class="btn btn-success w-100" @click="updateUser">
              Actualizar información
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Banco / Transacciones / Enlace de acceso -->
    <div class="row">
      <!-- Información bancaria -->
      <div class="col-lg-6 mb-4" v-if="bank_information">
        <div class="card h-100">
          <div class="card-header pb-0">
            <h6>Información bancaria</h6>
            <span class="text-muted small">(El cliente la completa)</span>
          </div>
          <div class="card-body px-4">
            <label class="font-weight-bold">Banco</label>
            <p class="form-control">{{ bank_information.bank }}</p>
            <label class="font-weight-bold">Número de cuenta</label>
            <p class="form-control">{{ bank_information.accountNumber }}</p>
            <label class="font-weight-bold">Tipo de cuenta</label>
            <p class="form-control mb-0">
              {{ getAccountTypeInSpanish(bank_information.accountType) }}
            </p>
          </div>
        </div>
      </div>

      <!-- Crear transacciones -->
      <div class="col-lg-6 mb-4">
        <div class="card h-100">
          <div class="card-header pb-0">
            <h6>Crear transacciones</h6>
          </div>
          <div class="card-body px-4">
            <p class="text-muted small mb-3">
              Esta herramienta genera de forma automática un historial de
              movimientos para el cliente. Usted define <b>cuántos</b>
              movimientos crear y <b>cuánto</b> deben sumar en total. El sistema
              reparte ese total en montos al azar (algunos positivos y otros
              negativos), de manera que la suma neta de todos los movimientos dé
              exactamente el monto indicado.
            </p>

            <label class="font-weight-bold">Cantidad de transacciones a crear</label>
            <input
              type="number"
              min="1"
              max="100"
              class="form-control"
              placeholder="Ej.: 20"
              v-model="transactions.total_transactions"
            />
            <small class="text-muted d-block mb-3">
              Cantidad de movimientos que se generarán. Cada uno tendrá un monto
              distinto al azar. Máximo 100.
            </small>

            <label class="font-weight-bold">Monto total (saldo neto final)</label>
            <input
              type="number"
              class="form-control"
              placeholder="Ej.: 5000"
              v-model="transactions.total_amount"
            />
            <small class="text-muted d-block mb-2">
              La suma de <b>todos</b> los movimientos dará exactamente este
              número. Se reparte en montos positivos y negativos al azar; lo
              único que se garantiza es que el balance final coincida con este
              monto.
            </small>

            <div
              v-if="transactions.total_transactions > 0 && transactions.total_amount != 0"
              class="alert alert-info py-2 px-3 text-sm mb-0"
            >
              Se crearán
              <b>{{ transactions.total_transactions }}</b> movimiento(s) que en
              total sumarán <b>{{ transactions.total_amount }}</b>.
            </div>
            <button
              class="btn btn-success w-100 mt-3"
              @click="createTransactions"
              :disabled="isCreatingTransactions"
            >
              <span v-if="isCreatingTransactions">
                <i class="fas fa-spinner fa-spin"></i> Procesando...
              </span>
              <span v-else>Crear transacciones</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Credenciales de acceso -->
      <div class="col-lg-6 mb-4" v-if="user && user.type === 'leads'">
        <div class="card h-100">
          <div class="card-header pb-0">
            <h6>Credenciales de acceso</h6>
          </div>
          <div class="card-body px-4">
            <div class="mb-3">
              <label class="font-weight-bold">Token de acceso</label>
              <div class="d-flex align-items-center">
                <input
                  type="text"
                  class="form-control"
                  :value="user.access_token"
                  readonly
                />
                <button
                  class="btn btn-sm bg-gradient-secondary mb-0 ms-2"
                  @click="copyCredential(user.access_token)"
                >
                  Copiar
                </button>
              </div>
            </div>

            <div class="mb-3">
              <label class="font-weight-bold">PIN</label>
              <div class="d-flex align-items-center">
                <input
                  type="text"
                  class="form-control"
                  :value="user.access_pin"
                  readonly
                />
                <button
                  class="btn btn-sm bg-gradient-secondary mb-0 ms-2"
                  @click="copyCredential(user.access_pin)"
                >
                  Copiar
                </button>
              </div>
            </div>

            <p class="text-sm text-muted mb-3">
              Estas credenciales son <b>permanentes</b> y no vencen nunca. El
              cliente ingresa al portal usando el <b>token</b> junto con el
              <b>PIN</b>.
            </p>

            <button class="btn bg-gradient-warning w-100" @click="regenerateAccess">
              Regenerar credenciales
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Documents Card (Compact Version) -->
    <div class="card mb-4 documents-card">
      <div
        class="card-header pb-2 d-flex justify-content-between align-items-center"
      >
        <div class="d-flex align-items-center">
          <div class="docs-icon-wrap me-2">
            <i class="fas fa-folder-open"></i>
          </div>
          <div>
            <h6 class="font-weight-bold m-0">Documentos</h6>
            <span class="text-xs text-secondary">Identidad, banco, archivos y selfie del usuario</span>
          </div>
        </div>
      </div>
      <div class="card-body p-3">
        <div class="row">
          <!-- Column 1: Identification Document -->
          <div class="col-md-6 col-xl-3 mb-3 px-2">
            <div class="document-section border rounded p-2 h-100">
              <!-- Header with badge -->
              <div
                class="d-flex justify-content-between align-items-center mb-2"
              >
                <h6 class="font-weight-bold text-primary mb-0 small">
                  Documento de identidad
                </h6>
                <span
                  class="badge badge-sm"
                  :class="{
                    'badge-success':
                      user_configuration &&
                      user_configuration.url_file &&
                      user_configuration.status,
                    'badge-warning':
                      user_configuration &&
                      user_configuration.url_file &&
                      !user_configuration.status,
                    'badge-secondary':
                      !user_configuration || !user_configuration.url_file,
                  }"
                >
                  {{
                    user_configuration && user_configuration.url_file
                      ? user_configuration.status
                        ? "Aprobado"
                        : "No aprobado"
                      : "No subido"
                  }}
                </span>
              </div>

              <!-- Preview -->
              <a
                v-if="user_configuration && user_configuration.url_file"
                :href="user_configuration.url_file"
                target="_blank"
                class="doc-preview"
              >
                <img
                  v-if="isImage(user_configuration.url_file)"
                  :src="user_configuration.url_file"
                  class="doc-thumb"
                />
                <span v-else class="doc-file">
                  <i class="fas fa-file-alt"></i> Ver documento
                </span>
              </a>
              <div v-else class="doc-empty">
                <i class="fas fa-image"></i> Sin subir
              </div>

              <!-- Actions -->
              <div
                class="text-center mt-2"
                v-if="
                  user_configuration &&
                  user_configuration.url_file &&
                  !user_configuration.status
                "
              >
                <button class="btn btn-danger btn-sm mb-0" @click="deleteFile">
                  <i class="fas fa-trash"></i>
                </button>
                <button class="btn btn-success btn-sm mb-0" @click="approveFile">
                  <i class="fas fa-check mr-1"></i> Aprobar
                </button>
              </div>
            </div>
          </div>

          <!-- Column 2: Bank Document -->
          <div class="col-md-6 col-xl-3 mb-3 px-2" v-if="bank_information">
            <div class="document-section border rounded p-2 h-100">
              <!-- Header with badge -->
              <div
                class="d-flex justify-content-between align-items-center mb-2"
              >
                <h6 class="font-weight-bold text-primary mb-0 small">
                  Documento bancario
                </h6>
                <span
                  class="badge badge-sm"
                  :class="{
                    'badge-success': bank_information.status,
                    'badge-warning': !bank_information.status,
                  }"
                >
                  {{ bank_information.status ? "Aprobado" : "No aprobado" }}
                </span>
              </div>

              <!-- Preview -->
              <a
                v-if="bank_information.url_file && bank_information.url_file !== 'false' && bank_information.url_file.startsWith('http')"
                :href="bank_information.url_file"
                target="_blank"
                class="doc-preview"
              >
                <img
                  v-if="isImage(bank_information.url_file)"
                  :src="bank_information.url_file"
                  class="doc-thumb"
                />
                <span v-else class="doc-file">
                  <i class="fas fa-file-alt"></i> Ver documento
                </span>
              </a>
              <div v-else class="doc-empty">
                <i class="fas fa-file-invoice"></i> Sin subir
              </div>

              <!-- Actions -->
              <div
                class="text-center mt-2"
                v-if="bank_information.url_file && bank_information.url_file !== 'false' && bank_information.url_file.startsWith('http') && !bank_information.status"
              >
                <button class="btn btn-danger btn-sm mb-0" @click="deleteBankFile">
                  <i class="fas fa-trash"></i>
                </button>
                <button class="btn btn-success btn-sm mb-0" @click="approveBankFile">
                  <i class="fas fa-check mr-1"></i> Aprobar
                </button>
              </div>
            </div>
          </div>

          <!-- Column 3: Downloadable File -->
          <div class="col-md-6 col-xl-3 mb-3 px-2">
            <div class="document-section border rounded p-2 h-100">
              <!-- Header with badge -->
              <div
                class="d-flex justify-content-between align-items-center mb-2"
              >
                <h6 class="font-weight-bold text-primary mb-0 small">
                  Archivo descargable
                </h6>
                <span
                  class="badge badge-sm"
                  :class="{
                    'badge-info':
                      user_configuration &&
                      user_configuration.url_file_to_download,
                    'badge-light': !(
                      user_configuration &&
                      user_configuration.url_file_to_download
                    ),
                  }"
                >
                  {{
                    user_configuration &&
                    user_configuration.url_file_to_download
                      ? "Archivo disponible"
                      : "Sin archivo"
                  }}
                </span>
              </div>

              <!-- Preview / acciones del archivo actual -->
              <a
                v-if="user_configuration && user_configuration.url_file_to_download"
                :href="user_configuration.url_file_to_download"
                target="_blank"
                class="doc-preview"
              >
                <img
                  v-if="isImage(user_configuration.url_file_to_download)"
                  :src="user_configuration.url_file_to_download"
                  class="doc-thumb"
                />
                <span v-else class="doc-file">
                  <i class="fas fa-download"></i> Ver archivo
                </span>
              </a>
              <div v-else class="doc-empty">
                <i class="fas fa-file-arrow-down"></i> Sin archivo
              </div>

              <div
                class="text-center my-2"
                v-if="user_configuration && user_configuration.url_file_to_download"
              >
                <button class="btn btn-danger btn-sm mb-0" @click="deleteDownloadFile">
                  <i class="fas fa-trash mr-1"></i> Eliminar
                </button>
              </div>

              <!-- File upload controls -->
              <div class="bg-light rounded p-2">
                <div class="custom-file mb-1">
                  <input
                    type="file"
                    class="custom-file-input"
                    id="downloadFileInput"
                    @change="handleDownloadFileUpload"
                  />
                  <label
                    class="custom-file-label text-truncate small"
                    for="downloadFileInput"
                    >{{ downloadFileName }}</label
                  >
                </div>
                <button
                  class="btn btn-primary btn-sm btn-block"
                  @click="uploadDownloadFile"
                  :disabled="!downloadFile"
                >
                  <i class="fas fa-upload mr-1"></i> Subir
                </button>
              </div>
            </div>
          </div>

          <!-- Column 4: Selfie -->
          <div class="col-md-6 col-xl-3 mb-3 px-2">
            <div class="document-section border rounded p-2 h-100">
              <!-- Header with badge -->
              <div
                class="d-flex justify-content-between align-items-center mb-2"
              >
                <h6 class="font-weight-bold text-primary mb-0 small">Selfie</h6>
                <span
                  class="badge badge-sm"
                  :class="{
                    'badge-success':
                      user_configuration &&
                      user_configuration.url_selfie &&
                      user_configuration.status,
                    'badge-warning':
                      user_configuration &&
                      user_configuration.url_selfie &&
                      !user_configuration.status,
                    'badge-secondary':
                      !user_configuration || !user_configuration.url_selfie,
                  }"
                >
                  {{
                    user_configuration && user_configuration.url_selfie
                      ? user_configuration.status
                        ? "Aprobado"
                        : "Pendiente"
                      : "No subido"
                  }}
                </span>
              </div>

              <!-- Preview -->
              <a
                v-if="user_configuration && user_configuration.url_selfie"
                :href="user_configuration.url_selfie"
                target="_blank"
                class="doc-preview"
              >
                <img
                  v-if="isImage(user_configuration.url_selfie)"
                  :src="user_configuration.url_selfie"
                  class="doc-thumb"
                />
                <span v-else class="doc-file">
                  <i class="fas fa-user"></i> Ver selfie
                </span>
              </a>
              <div v-else class="doc-empty">
                <i class="fas fa-user"></i> Sin subir
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Certificate Generator Card -->
    <div class="card mb-4 documents-card">
      <div class="card-header pb-2 d-flex align-items-center">
        <div class="docs-icon-wrap me-2">
          <i class="fas fa-file-signature"></i>
        </div>
        <div>
          <h6 class="font-weight-bold m-0">Generar certificado</h6>
          <span class="text-xs text-secondary">Certificación de legalidad y procedencia de fondos (PDF)</span>
        </div>
      </div>
      <div class="card-body p-3">
        <div class="row">
          <div class="col-md-3 mb-3">
            <label class="form-label text-xs font-weight-bold mb-1">Fecha</label>
            <input
              type="text"
              class="form-control form-control-sm"
              v-model="certificate.fecha"
              placeholder="17/06/2026"
            />
          </div>
          <div class="col-md-5 mb-3">
            <label class="form-label text-xs font-weight-bold mb-1">
              Nombre (Apellido Nombre)
            </label>
            <input
              type="text"
              class="form-control form-control-sm"
              v-model="certificate.nombre"
              placeholder="Negoescu Rodica"
            />
          </div>
          <div class="col-md-4 mb-3">
            <label class="form-label text-xs font-weight-bold mb-1">
              Documento de identidad
            </label>
            <input
              type="text"
              class="form-control form-control-sm"
              v-model="certificate.documento"
              placeholder="X6684662B"
            />
          </div>
          <div class="col-md-3 mb-3">
            <label class="form-label text-xs font-weight-bold mb-1">Nacionalidad</label>
            <input
              type="text"
              class="form-control form-control-sm"
              v-model="certificate.nacionalidad"
              placeholder="ESPAÑOLA"
            />
          </div>
          <div class="col-md-3 mb-3">
            <label class="form-label text-xs font-weight-bold mb-1">
              Capital recuperado
            </label>
            <input
              type="text"
              class="form-control form-control-sm"
              v-model="certificate.capital"
              placeholder="$91,501 USD"
            />
          </div>
          <div class="col-md-3 mb-3">
            <label class="form-label text-xs font-weight-bold mb-1">
              Costo de inicio
            </label>
            <input
              type="text"
              class="form-control form-control-sm"
              v-model="certificate.costo"
              placeholder="$319 USD"
            />
          </div>
          <div class="col-md-3 mb-3">
            <label class="form-label text-xs font-weight-bold mb-1">
              Monto (tabla)
            </label>
            <input
              type="text"
              class="form-control form-control-sm"
              v-model="certificate.montoTabla"
              placeholder="$ 91,501"
            />
          </div>
          <div class="col-md-5 mb-3">
            <label class="form-label text-xs font-weight-bold mb-1">
              Beneficiario (firma)
            </label>
            <input
              type="text"
              class="form-control form-control-sm"
              v-model="certificate.beneficiario"
              placeholder="Rodica Negoescu"
            />
          </div>
        </div>
        <div class="text-end">
          <button
            class="btn btn-primary btn-sm mb-0"
            @click="downloadCertificate"
            :disabled="generatingCertificate"
          >
            <i class="fas fa-file-pdf me-1"></i>
            {{ generatingCertificate ? "Generando..." : "Generar PDF" }}
          </button>
        </div>
      </div>
    </div>

    <!-- User Messages Card -->
    <div class="card mb-4">
      <div class="card-header pb-0">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h6 class="text-uppercase mb-0">Mensajes del usuario</h6>
          <button class="btn btn-primary btn-sm" @click="showMessageModal">
            <i class="fas fa-plus"></i> Nuevo mensaje
          </button>
        </div>

        <!-- Configuración del cliente -->
        <div class="row mb-2" v-if="user_configuration">
          <div class="col-md-6 mb-2">
            <div
              class="d-flex justify-content-between align-items-center bg-light p-2 rounded h-100"
            >
              <div class="form-check mb-0">
                <input
                  type="checkbox"
                  class="form-check-input"
                  id="canSeePaymentInfo"
                  v-model="user_configuration.can_add_bank_info"
                />
                <label
                  class="form-check-label font-weight-bold"
                  for="canSeePaymentInfo"
                >
                  Permitir ver información bancaria
                </label>
              </div>
              <button
                class="btn btn-success btn-sm mb-0"
                @click="updateUserConfiguration"
              >
                Actualizar
              </button>
            </div>
          </div>

          <div class="col-md-6 mb-2">
            <div
              class="d-flex justify-content-between align-items-center bg-light p-2 rounded h-100"
            >
              <div class="form-check mb-0">
                <input
                  type="checkbox"
                  class="form-check-input"
                  id="canReceiveNotifications"
                  v-model="user_configuration.can_receive_notifications"
                />
                <label
                  class="form-check-label font-weight-bold"
                  for="canReceiveNotifications"
                >
                  Mostrar notificacion de bloqueo
                </label>
              </div>
              <button
                class="btn btn-success btn-sm mb-0"
                @click="updateUserConfiguration"
              >
                Actualizar
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="card-body">
        <div v-if="user_messages.length === 0" class="text-center py-4">
          <p>No se encontraron mensajes para este usuario</p>
        </div>
        <div v-else class="table-responsive user-messages-table-wrapper">
          <table class="table table-bordered table-sm user-messages-table">
            <thead>
              <tr>
                <th
                  class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                >
                  Título
                </th>
                <th
                  class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                >
                  Mensaje
                </th>
                <th
                  class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                >
                  Monto
                </th>
                <th
                  class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                >
                  Información de pago
                </th>
                <th
                  class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                >
                  Puede añadir archivo
                </th>
                <th
                  class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                >
                  Ver info. de pago
                </th>
                <th
                  class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                >
                  Archivo
                </th>
                <th
                  class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                >
                  Estado
                </th>
                <th
                  class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                >
                  Fecha
                </th>
                <th
                  class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                >
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(message, index) in user_messages"
                :key="index"
                class="user-message-row"
              >
                <td class="message-title-cell">
                  <p class="text-xs font-weight-bold mb-0">
                    {{ message.title }}
                  </p>
                </td>
                <td class="message-body-cell">
                  <p class="text-xs mb-0">
                    {{ message.body }}
                  </p>
                </td>
                <td>
                  <p class="text-xs font-weight-bold mb-0">
                    ${{
                      message.amount
                        ? $filters.formatNumber(message.amount)
                        : "0.00"
                    }}
                  </p>
                </td>
                <td class="message-payment-info-cell">
                  <p class="text-xs mb-0" v-html="message.payment_info"></p>
                </td>
                <td>
                  <span
                    class="text-xs mb-0 text-dark badge"
                    :class="
                      message.can_add_file ? 'badge-info' : 'badge-secondary'
                    "
                  >
                    {{ message.can_add_file ? "Sí" : "No" }}
                  </span>
                </td>
                <td>
                  <span
                    class="text-xs mb-0 text-dark badge"
                    :class="
                      message.can_see_payment_info
                        ? 'badge-info'
                        : 'badge-secondary'
                    "
                  >
                    {{ message.can_see_payment_info ? "Sí" : "No" }}
                  </span>
                </td>
                <td>
                  <p class="text-xs text-center mb-0" v-if="!message.url_file">
                    -------
                  </p>
                  <a
                    v-else
                    :href="message.url_file"
                    target="_blank"
                    class="btn btn-info btn-sm"
                  >
                    <i class="fas fa-eye mr-1"></i> Ver
                  </a>
                </td>
                <td>
                  <span
                    class="text-xs mb-0 text-dark badge"
                    :class="message.status ? 'badge-success' : 'badge-warning'"
                  >
                    {{ message.status ? "Aprobado" : "No aprobado" }}
                  </span>
                </td>
                <td>
                  <p class="text-xs mb-0">
                    {{ $filters.formatDate(message.created_at) }}
                  </p>
                </td>
                <td>
                  <button
                    class="btn btn-danger btn-sm"
                    @click="deleteUserMessage(message._id)"
                  >
                    <i class="fas fa-trash"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="card mb-4">
      <div class="card-body">
        <table class="table table-bordered table-sm">
          <thead>
            <tr>
              <th
                class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
              >
                Agente
              </th>
              <th
                class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
              >
                monto
              </th>
              <th
                class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
              >
                descripción
              </th>
              <th
                class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
              >
                referencia
              </th>
              <th
                class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
              >
                creado en
              </th>
              <th
                class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
              >
                opciones
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, key) in array_transactions" :key="key">
              <td>
                <p class="text-uppercase text-xs font-weight-bold mb-0">
                  {{ item.agent.name }}
                </p>
              </td>
              <td>
                <p class="text-uppercase text-xs font-weight-bold mb-0">
                  ${{ $filters.formatNumber(item.amount) }}
                </p>
              </td>
              <td>
                <p class="text-uppercase text-xs font-weight-bold mb-0">
                  {{ item.description }}
                </p>
              </td>
              <td>
                <p class="text-uppercase text-xs font-weight-bold mb-0">
                  {{ item.reference }}
                </p>
              </td>
              <td>
                <p class="text-xs font-weight-bold mb-0">
                  {{ $filters.formatDate(item.created_at) }}
                </p>
              </td>
              <td>
                <button
                  class="btn btn-danger btn-sm mb-0"
                  @click="removeTransaction(item._id)"
                >
                  Eliminar
                </button>
                <button
                  class="btn btn-success btn-sm mb-0"
                  @click="showModal(item)"
                >
                  Actualizar
                </button>
              </td>
            </tr>
            <tr>
              <td colspan="4">
                <h5 class="text-uppercase text-end font-weight-bold mb-0">
                  Total:
                </h5>
              </td>
              <td colspan="2">
                <h5 class="text-uppercase text-start font-weight-bold mb-0">
                  ${{ $filters.formatNumber(sum_transactions) }}
                </h5>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Transaction Modal -->
    <div class="modal" v-if="modalVisible">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Actualizar transacción</h5>
            <button
              type="button"
              class="btn-close"
              @click="modalVisible = false"
            ></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label for="amount" class="form-label">Monto</label>
              <input
                type="number"
                class="form-control"
                id="amount"
                v-model="transaction_selected.amount"
              />
            </div>
            <div class="mb-3">
              <label for="description" class="form-label">Descripción</label>
              <input
                type="text"
                class="form-control"
                id="description"
                v-model="transaction_selected.description"
              />
            </div>
            <div class="mb-3">
              <label for="reference" class="form-label">Referencia</label>
              <input
                type="text"
                class="form-control"
                id="reference"
                v-model="transaction_selected.reference"
              />
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="modalVisible = false"
            >
              Cerrar
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="updateTransaction"
            >
              Guardar cambios
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- User Message Modal -->
    <div class="modal" v-if="message_modal_visible">
      <div class="modal-dialog modal-lg">
        <div
          class="modal-content"
          style="width: 60vw; max-width: 100% !important"
        >
          <div class="modal-header py-2">
            <h5 class="modal-title">Crear nuevo mensaje</h5>
            <button
              type="button"
              class="btn-close"
              @click="closeMessageModal"
            ></button>
          </div>
          <div class="modal-body p-3">
            <div class="row g-2">
              <!-- Title and Amount in one row -->
              <div class="col-md-8 mb-2">
                <label for="message_title" class="form-label small mb-1"
                  >Título</label
                >
                <input
                  type="text"
                  class="form-control form-control-sm"
                  id="message_title"
                  v-model="new_message.title"
                  placeholder="Ingrese título del mensaje"
                />
              </div>
              <div class="col-md-4 mb-2">
                <label for="message_amount" class="form-label small mb-1"
                  >Monto</label
                >
                <input
                  type="number"
                  class="form-control form-control-sm"
                  id="message_amount"
                  v-model="new_message.amount"
                  placeholder="0.00"
                />
                <small class="text-danger"
                  >Si es 0, no se muestra al cliente</small
                >
              </div>

              <!-- Message body -->
              <div class="col-12 mb-2">
                <label for="message_body" class="form-label small mb-1"
                  >Mensaje</label
                >
                <textarea
                  class="form-control form-control-sm"
                  id="message_body"
                  v-model="new_message.body"
                  rows="3"
                  placeholder="Ingrese contenido del mensaje"
                ></textarea>
              </div>

              <!-- Payment info -->
              <div class="col-12 mb-2">
                <label for="payment_info" class="form-label small mb-1"
                  >Información de pago</label
                >
                <ckeditor
                  v-model="new_message.paymentInfo"
                  style="min-height: 120px"
                ></ckeditor>
              </div>

              <!-- Checkboxes in a single row -->
              <div class="col-12">
                <div class="d-flex gap-4 mt-2">
                  <div class="form-check">
                    <input
                      type="checkbox"
                      class="form-check-input"
                      id="can_add_file"
                      v-model="new_message.canAddFile"
                    />
                    <label class="form-check-label" for="can_add_file"
                      >Permitir añadir archivo</label
                    >
                  </div>
                  <div class="form-check">
                    <input
                      type="checkbox"
                      class="form-check-input"
                      id="can_see_payment_info"
                      v-model="new_message.canSeePaymentInfo"
                    />
                    <label class="form-check-label" for="can_see_payment_info"
                      >Permitir ver info. de pago</label
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="closeMessageModal"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="createUserMessage"
            >
              Enviar mensaje
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { get, post } from "@/utils/httpRequest";
import uploadFile from "@/utils/uploadFile";
import { component as ckeditor } from "@mayasabha/ckeditor4-vue3";
import { generateCertificate } from "@/utils/generateCertificate";

export default {
  name: "index",
  components: {
    ckeditor,
  },
  data() {
    return {
      user: null,
      user_id: "",
      user_data: {},
      user_configuration: null,
      bank_information: null,
      array_transactions: [],
      sum_transactions: 0,
      transaction_selected: {},
      transactions: {
        total_transactions: 0,
        total_amount: 0,
      },
      isCreatingTransactions: false,
      transaction_id: "",
      transaction_amount: 0,
      transaction_description: "",
      transaction_reference: "",
      // User messages data
      user_messages: [],
      last_message: null,
      new_message: {
        title: "",
        body: "",
        amount: 0,
        paymentInfo: "",
        canAddFile: false,
        canSeePaymentInfo: false,
      },
      message_modal_visible: false,
      modalVisible: false,
      // Download file upload data
      downloadFile: null,
      downloadFileName: "Elegir archivo...",
      url_file: null,
      // Lista de agentes para la asignación (solo la cargan admin/supervisor).
      agents: [],
      // Certificate generator form (prefilled with sample values).
      generatingCertificate: false,
      certificate: {
        fecha: "17/06/2026",
        nombre: "Negoescu Rodica",
        documento: "X6684662B",
        nacionalidad: "ESPAÑOLA",
        capital: "$91,501 USD",
        costo: "$319 USD",
        montoTabla: "$ 91,501",
        beneficiario: "Rodica Negoescu",
      },
    };
  },
  mounted() {
    this.user_id = this.$route.query.id;
    this.getProfile();
    this.getTransactions();
    this.getUserConfiguration();
    this.getBankInformation();
    this.getUserMessages();
    this.getLastUserMessage();
    if (this.$userCan(["administrator", "supervisor"])) {
      this.getAgents();
    }
  },
  methods: {
    getAccountTypeInSpanish(accountType) {
      const accountTypes = {
        savings: "Cuenta de ahorro",
        checking: "Cuenta corriente",
        payroll: "Cuenta de nómina",
        term_deposit: "Cuenta de depósito a plazo (CDT / plazo fijo)",
        money_market: "Cuenta monetaria (money market)",
        basic_digital: "Cuenta básica / digital",
        joint: "Cuenta conjunta",
        business: "Cuenta empresarial / corporativa",
        foreign_currency: "Cuenta en divisas / multimoneda",
      };

      return accountTypes[accountType] || "Tipo de cuenta no especificado";
    },

    // Detecta si la URL apunta a una imagen (ignora el query de la presigned URL).
    isImage(url) {
      if (!url) return false;
      const clean = String(url).split("?")[0].toLowerCase();
      return /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(clean);
    },

    async downloadCertificate() {
      if (this.generatingCertificate) return;
      this.generatingCertificate = true;
      try {
        await generateCertificate({ ...this.certificate });
      } catch (error) {
        console.error("Error generating certificate:", error);
        this.$swal({
          icon: "error",
          title: "No se pudo generar el PDF",
          text: "Ocurrió un error al crear el certificado. Intentá nuevamente.",
        });
      } finally {
        this.generatingCertificate = false;
      }
    },

    copyToClipboard(text) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          this.$swal({
            html: "¡Token copiado al portapapeles!",
            icon: "success",
            timer: 1500,
            showCancelButton: false,
            showConfirmButton: false,
          });
        })
        .catch((err) => {
          console.error("Failed to copy text: ", err);
          this.$swal({
            html: "Error al copiar al portapapeles",
            icon: "error",
            timer: 1500,
            showCancelButton: false,
            showConfirmButton: false,
          });
        });
    },
    copyCredential(text) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          this.$swal({
            html: "¡Copiado al portapapeles!",
            icon: "success",
            timer: 1200,
            showCancelButton: false,
            showConfirmButton: false,
          });
        })
        .catch((err) => {
          console.error("Failed to copy text: ", err);
          this.$swal({
            html: "Error al copiar al portapapeles",
            icon: "error",
            timer: 1200,
            showCancelButton: false,
            showConfirmButton: false,
          });
        });
    },
    async regenerateAccess() {
      this.$swal({
        html: "¿Regenerar el token y PIN de este lead? Las credenciales anteriores dejarán de funcionar.",
        showCancelButton: true,
        confirmButtonText: "SÍ, regenerar",
        cancelButtonText: "Cancelar",
      }).then(async (result) => {
        if (!result.isConfirmed) return;
        const { data } = await post(
          "users/regenerate-access",
          { _id: this.user_id },
          true,
        );
        if (data && data.statusCode === 200) {
          this.user.access_token = data.access_token;
          this.user.access_pin = data.access_pin;
          this.$swal({
            html: "Credenciales regeneradas con éxito",
            icon: "success",
            timer: 2500,
            showCancelButton: false,
            showConfirmButton: false,
          });
        } else {
          this.$swal({
            html: (data && data.message) || "No se pudieron regenerar las credenciales",
            icon: "error",
            timer: 2500,
            showCancelButton: false,
            showConfirmButton: false,
          });
        }
      });
    },
    showModal(item) {
      this.transaction_selected = {
        _id: item._id,
        amount: item.amount,
        description: item.description,
        reference: item.reference,
      };
      this.modalVisible = true;
    },
    hideModal() {
      this.clearTransaction();
      this.modalVisible = false;
    },
    clearTransaction() {
      this.transaction_selected = {
        _id: "",
        amount: 0,
        description: "",
        reference: "",
      };
    },
    async getProfile() {
      const { data } = await get(`users/find-one?id=${this.user_id}`);
      data.password = "";
      this.user = data;
    },

    async getAgents() {
      const { data } = await get(`users/agents`, null, true);
      this.agents = Array.isArray(data) ? data : [];
    },

    async assignAgent() {
      const { data } = await post("users/assign-agent", {
        _id: this.user_id,
        agent_id: this.user.assigned_agent || "",
      });
      if (data && data.statusCode === 200) {
        this.$swal({
          html: "Agente actualizado con éxito",
          icon: "success",
          timer: 2000,
          showCancelButton: false,
          showConfirmButton: false,
        });
        await this.getProfile();
      } else {
        this.$swal({
          html: (data && data.message) || "No se pudo asignar el agente",
          icon: "error",
          timer: 2500,
          showCancelButton: false,
          showConfirmButton: false,
        });
      }
    },

    async getTransactions() {
      const { data } = await get(`traced-capital/by-user?id=${this.user_id}`);
      this.array_transactions = data;
      this.sum_transactions = this.array_transactions.reduce(
        (a, b) => a + b.amount,
        0,
      );
    },

    async updateUser() {
      const { data } = await post("users/update", {
        ...this.user,
        _id: this.user_id,
      });
      if ("error" in data) {
        this.$swal({
          html: data.message,
          icon: "error",
          timer: 2000,
          showCancelButton: false,
          showConfirmButton: false,
        });
        return;
      }
      await this.getProfile();
      this.$swal({
        html: "Proceso realizado con éxito",
        icon: "success",
        timer: 2000,
        showCancelButton: false,
        showConfirmButton: false,
      });
    },
    async createTransactions() {
      // Prevent double clicks by checking if already processing
      if (this.isCreatingTransactions) return;

      if (
        this.transactions.total_transactions >= 1 &&
        this.transactions.total_amount !== 0
      ) {
        if (this.transactions.total_transactions > 100) {
          this.$swal({
            html: "El número máximo de transacciones es 100",
            icon: "error",
            timer: 2000,
            showCancelButton: false,
            showConfirmButton: false,
          });
          return;
        }

        try {
          this.isCreatingTransactions = true;
          await post("traced-capital/create", {
            ...this.transactions,
            _id: this.user_id,
          });
          await this.getTransactions();
          this.transactions = {
            total_transactions: 0,
            total_amount: 0,
          };
          this.$swal({
            html: "Proceso realizado con éxito",
            icon: "success",
            timer: 2000,
            showCancelButton: false,
            showConfirmButton: false,
          });
        } catch (error) {
          console.error("Error creating transactions:", error);
          this.$swal({
            html: "Error al crear transacciones",
            icon: "error",
            timer: 2000,
            showCancelButton: false,
            showConfirmButton: false,
          });
        } finally {
          this.isCreatingTransactions = false;
        }
      } else {
        this.$swal({
          html: "Error. Por favor verifique de nuevo",
          icon: "error",
          timer: 2000,
          showCancelButton: false,
          showConfirmButton: false,
        });
      }
    },
    async removeTransaction(id) {
      await post("traced-capital/remove", { id: id });
      await this.getTransactions();
      this.$swal({
        html: "Proceso realizado con éxito",
        icon: "success",
        timer: 2000,
        showCancelButton: false,
        showConfirmButton: false,
      });
    },
    async updateTransaction() {
      if (
        this.transaction_selected.amount >= 1 &&
        this.transaction_selected.description !== "" &&
        this.transaction_selected.reference !== ""
      ) {
        await post("traced-capital/update", this.transaction_selected);
        this.hideModal();
        await this.getTransactions();
        this.$swal({
          html: "Proceso realizado con éxito",
          icon: "success",
          timer: 2000,
          showCancelButton: false,
          showConfirmButton: false,
        });
      }
    },
    async getUserConfiguration() {
      const { data } = await get(
        `auth/get-user-config-by-id?id=${this.user_id}`,
      );
      this.user_configuration = data;
      console.log("----------> user configuration ", this.user_configuration);
    },

    async updateUserConfiguration() {
      try {
        console.log("----------> user configuration ", this.user_configuration);

        // Update can_add_bank_info field
        await post("user-configurations/update/can-add-bank-info", {
          _id: this.user_configuration.user_id,
          can_add_bank_info: this.user_configuration.can_add_bank_info,
        });

        // Update can_receive_notifications field
        await post("user-configurations/update/can-receive-notifications", {
          _id: this.user_configuration.user_id,
          can_receive_notifications:
            this.user_configuration.can_receive_notifications,
        });

        this.$swal({
          html: "Configuración actualizada exitosamente",
          icon: "success",
          timer: 2000,
          showCancelButton: false,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error("Error updating user configuration:", error);
        this.$swal({
          html: "Error al actualizar la configuración",
          icon: "error",
          timer: 2000,
          showCancelButton: false,
          showConfirmButton: false,
        });
      }
    },
    async approveFile() {
      this.$swal({
        html: "¿Está seguro de que desea aprobar el documento?",
        showCancelButton: true,
        confirmButtonText: "SÍ",
        cancelButtonText: `NO`,
      }).then(async (result) => {
        if (result.isConfirmed) {
          await post("user-configurations/approve-by-user-id", {
            _id: this.user_id,
          });
          window.location.reload();
        }
      });
    },
    async deleteFile() {
      this.$swal({
        html: "¿Está seguro de que desea eliminar el documento?",
        showCancelButton: true,
        confirmButtonText: "SÍ",
        cancelButtonText: `NO`,
      }).then(async (result) => {
        if (result.isConfirmed) {
          await post("user-configurations/delete-by-user-id", {
            _id: this.user_id,
          });
          window.location.reload();
        }
      });
    },
    async deleteBankFile() {
      this.$swal({
        html: "¿Está seguro de que desea eliminar el documento bancario?",
        showCancelButton: true,
        confirmButtonText: "SÍ",
        cancelButtonText: `NO`,
      }).then(async (result) => {
        if (result.isConfirmed) {
          await post("bank-information/delete-bank-info-file", {
            _id: this.bank_information.userId,
          });
          window.location.reload();
        }
      });
    },
    async approveBankFile() {
      this.$swal({
        html: "¿Está seguro de que desea aprobar el documento bancario?",
        showCancelButton: true,
        confirmButtonText: "SÍ",
        cancelButtonText: `NO`,
      }).then(async (result) => {
        if (result.isConfirmed) {
          await post("bank-information/approve-bank-info", {
            _id: this.bank_information.userId,
          });
          window.location.reload();
        }
      });
    },

    // Download file handling methods
    handleDownloadFileUpload(event) {
      this.selectImage(event.target);
    },

    async selectImage(e) {
      this.url_file = e.files[0];
      if (this.url_file) {
        this.downloadFile = this.url_file;
        this.downloadFileName = this.url_file.name;
      } else {
        this.downloadFile = null;
        this.downloadFileName = "Elegir archivo...";
      }
    },

    async uploadDownloadFile() {
      if (!this.url_file) {
        this.$swal({
          html: "Por favor seleccione un archivo para subir",
          icon: "error",
          timer: 2000,
          showCancelButton: false,
          showConfirmButton: false,
        });
        return;
      }

      try {
        // Show loading indicator
        this.$swal({
          html: "Subiendo archivo...",
          allowOutsideClick: false,
          didOpen: () => {
            this.$swal.showLoading();
          },
        });

        // Use the uploadFile.upload method as specified
        const { data } = await uploadFile.upload(
          "user-configurations/upload-download-file",
          {
            file: this.url_file,
            _id: this.user_id,
          },
        );

        if (data && data.error) {
          throw new Error(data.message || "Error al subir el archivo");
        }

        // Refresh user configuration data
        await this.getUserConfiguration();

        // Reset file input
        this.url_file = null;
        this.downloadFile = null;
        this.downloadFileName = "Elegir archivo...";
        document.getElementById("downloadFileInput").value = "";

        // Show success message
        this.$swal({
          html: "Archivo subido con éxito",
          icon: "success",
          timer: 2000,
          showCancelButton: false,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error("Error uploading file:", error);
        this.$swal({
          html: error.message || "Error al subir el archivo",
          icon: "error",
          timer: 2000,
          showCancelButton: false,
          showConfirmButton: false,
        });
      }
    },

    async deleteDownloadFile() {
      try {
        this.$swal({
          title: "¿Estás seguro?",
          text: "¿Deseas eliminar este archivo descargable?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Sí, eliminar",
          cancelButtonText: "Cancelar",
        }).then(async (result) => {
          if (result.isConfirmed) {
            // Show loading indicator
            this.$swal({
              html: "Eliminando archivo...",
              allowOutsideClick: false,
              didOpen: () => {
                this.$swal.showLoading();
              },
            });

            // Call API to delete the downloadable file
            await post("user-configurations/delete-download-file", {
              _id: this.user_id,
            });

            // Refresh user configuration data
            await this.getUserConfiguration();

            // Show success message
            this.$swal({
              html: "Archivo eliminado correctamente",
              icon: "success",
              timer: 2000,
              showCancelButton: false,
              showConfirmButton: false,
            });
          }
        });
      } catch (error) {
        console.error("Error deleting downloadable file:", error);
        this.$swal({
          html: "Error al eliminar el archivo",
          icon: "error",
          timer: 2000,
          showCancelButton: false,
          showConfirmButton: false,
        });
      }
    },

    async getBankInformation() {
      const { data } = await get(
        `auth/get-bank-info-by-id?id=${this.$route.query.id}`,
      );
      if (data) {
        this.bank_information = {
          bank: data.name,
          country: data.country,
          accountNumber: data.account_number,
          accountType: data.account_type,
          status: data.status,
          userId: data.user_id,
          url_file: data.url_file,
        };
      }
    },

    // User Messages Methods
    async getUserMessages() {
      try {
        const { data } = await get(
          `user-message/find-all-by-user?id=${this.user_id}`,
        );
        if (data) {
          this.user_messages = data;
        }
      } catch (error) {
        console.error("Error fetching user messages:", error);
        this.$swal({
          html: "Error al obtener los mensajes del usuario",
          icon: "error",
          timer: 2000,
          showCancelButton: false,
          showConfirmButton: false,
        });
      }
    },

    async getLastUserMessage() {
      try {
        const { data } = await get(
          `user-message/last-message-by-user?id=${this.user_id}`,
        );
        if (data) {
          this.last_message = data;
        }
      } catch (error) {
        console.error("Error fetching last message:", error);
      }
    },

    async createUserMessage() {
      try {
        if (!this.new_message.title || !this.new_message.body) {
          this.$swal({
            html: "Por favor complete el título y el cuerpo del mensaje",
            icon: "error",
            timer: 2000,
            showCancelButton: false,
            showConfirmButton: false,
          });
          return;
        }

        await post("user-message/create", {
          userId: this.user_id,
          title: this.new_message.title,
          body: this.new_message.body,
          amount: this.new_message.amount || 0,
          paymentInfo: this.new_message.paymentInfo || "",
          canAddFile: this.new_message.canAddFile,
          canSeePaymentInfo: this.new_message.canSeePaymentInfo,
        });

        // Reset form and refresh messages
        this.new_message = {
          title: "",
          body: "",
          amount: 0,
          paymentInfo: "",
          canAddFile: false,
          canSeePaymentInfo: false,
        };
        this.message_modal_visible = false;

        await this.getUserMessages();
        await this.getLastUserMessage();

        this.$swal({
          html: "Mensaje creado con éxito",
          icon: "success",
          timer: 2000,
          showCancelButton: false,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error("Error creating message:", error);
        this.$swal({
          html: "Error al crear el mensaje",
          icon: "error",
          timer: 2000,
          showCancelButton: false,
          showConfirmButton: false,
        });
      }
    },

    async deleteUserMessage(messageId) {
      try {
        this.$swal({
          html: "¿Está seguro de que desea eliminar este mensaje?",
          showCancelButton: true,
          confirmButtonText: "SÍ",
          cancelButtonText: `NO`,
        }).then(async (result) => {
          if (result.isConfirmed) {
            await post("user-message/delete", {
              id: messageId,
            });

            await this.getUserMessages();
            await this.getLastUserMessage();

            this.$swal({
              html: "Mensaje eliminado con éxito",
              icon: "success",
              timer: 2000,
              showCancelButton: false,
              showConfirmButton: false,
            });
          }
        });
      } catch (error) {
        console.error("Error deleting message:", error);
        this.$swal({
          html: "Error al eliminar el mensaje",
          icon: "error",
          timer: 2000,
          showCancelButton: false,
          showConfirmButton: false,
        });
      }
    },

    showMessageModal() {
      this.message_modal_visible = true;
    },

    closeMessageModal() {
      this.message_modal_visible = false;
      this.new_message = {
        title: "",
        body: "",
        amount: 0,
        paymentInfo: "",
        canAddFile: false,
        canSeePaymentInfo: false,
      };
    },
  },
};
</script>

<style scoped>
/* Tarjetas de documentos con preview */
.documents-card .docs-icon-wrap {
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: linear-gradient(310deg, #5e72e4, #825ee4);
  color: #fff;
  font-size: 1rem;
  flex-shrink: 0;
}

.document-section {
  display: flex;
  flex-direction: column;
  min-height: 210px;
  background: #fff;
  transition: box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease;
}
.document-section:hover {
  box-shadow: 0 8px 20px rgba(20, 20, 70, 0.08);
  transform: translateY(-2px);
  border-color: #cdd4f0 !important;
}
.document-section h6.small {
  font-size: 0.78rem;
  letter-spacing: 0.2px;
}
.doc-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-grow: 1;
  overflow: hidden;
  text-decoration: none;
  border-radius: 6px;
}
.doc-thumb {
  max-width: 100%;
  max-height: 130px;
  object-fit: contain;
  border-radius: 6px;
  border: 1px solid #eee;
}
.doc-file,
.doc-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-grow: 1;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #8392ab;
  text-decoration: none;
}
.doc-file i,
.doc-empty i {
  font-size: 2rem;
}
.doc-empty {
  border: 1px dashed #d2d6da;
  border-radius: 6px;
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal-content {
  background-color: white;
  width: 400px;
  padding: 20px;
  border-radius: 8px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.modal-header h3 {
  margin: 0;
}

.close-button {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #777;
  cursor: pointer;
}

.modal-body {
  margin-bottom: 10px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
}

.user-messages-table-wrapper {
  overflow-x: auto;
}

.user-messages-table {
  min-width: 1400px;
}

.user-message-row td {
  vertical-align: top;
}

.message-title-cell {
  min-width: 180px;
}

.message-body-cell {
  min-width: 420px;
}

.message-payment-info-cell {
  min-width: 280px;
}

.message-body-cell p,
.message-payment-info-cell p {
  white-space: normal;
  word-break: break-word;
  line-height: 1.5;
}

.message-payment-info-cell :deep(*) {
  white-space: normal;
  word-break: break-word;
}

</style>
